import {forwardRef, ReactElement, Ref, useContext} from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  DialogProps as MuiDialogProps,
  IconButton,
  Stack,
  SxProps,
  Box,
  Slide,
  Divider,
  Tooltip,
  Theme,
} from '@mui/material';
import {LoadingButton, LoadingButtonProps as MuiLoadingButtonProps} from '@mui/lab';
import {Close} from '@mui/icons-material';
import {TransitionProps} from '@mui/material/transitions';
import {useTranslation} from 'react-i18next';
import {translations} from '../../i18n/translations';
import {FiInfo, FiTrash2} from 'react-icons/fi';
import {Pallete} from '../../theme/bluebird';
import {AppContext} from '../../contexts/app/context';
import {AppContextType} from '../../contexts/app/types';
import {ThemeMode} from '../../enums/theme-mode';

type ActionTypes = 'destructive' | 'nondestructive' | 'neutral';

type UnionOverrideKeys<T, U> = Omit<T, keyof U> & U;

export type ModalAction = UnionOverrideKeys<
  MuiLoadingButtonProps,
  {label: string; type: ActionTypes; buttonType?: 'submit' | 'button'; textUpperCase?: boolean}
>;

export interface ModalProps extends MuiDialogProps {
  title?: string;
  actions: ModalAction[];
  preventCloseClick?: boolean;
  actionButton?: {
    type: 'destroy' | 'close' | 'info';
    callback?: () => void;
    preventAutoClose?: boolean;
  };
  readonly?: boolean;
  sx?: SxProps<Theme>;
}

const Transition = forwardRef(function Transition(
  props: TransitionProps & {
    children: ReactElement<any, any>;
  },
  ref: Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export const Modal = ({
  id,
  open,
  onClose,
  title,
  actions,
  children,
  preventCloseClick,
  actionButton,
  readonly,
  ...rest
}: ModalProps) => {
  const appContext: AppContextType = useContext(AppContext);

  // Translations
  const {t} = useTranslation();

  const DEFAULT_BUTTON_ACTIONS = {
    close: (
      <IconButton
        onClick={() => {
          if (actionButton?.callback) actionButton.callback();
          if (actionButton?.preventAutoClose) return;
          // @ts-ignore
          onClose();
        }}
        sx={{color: Pallete.gray.base, opacity: 0.5}}
      >
        <Close sx={{margin: 0, padding: 0}} />
      </IconButton>
    ),
    destroy: (
      <IconButton
        onClick={() => {
          if (actionButton?.callback) actionButton.callback();
          if (actionButton?.preventAutoClose) return;
          // @ts-ignore
          onClose();
        }}
        sx={{
          width: '40px',
          height: '40px',
          borderRadius: '5px',
          color: Pallete.gray.base,
        }}
      >
        <FiTrash2 style={{margin: 0, padding: 0, color: 'currentcolor', width: '16px'}} />
      </IconButton>
    ),
    info: (
      <Tooltip title={t(translations.activities_type_entry.modals.view.view_only)}>
        <IconButton
          sx={{
            ':hover': {
              backgroundColor: 'transparent',
            },
          }}
        >
          <FiInfo style={{margin: 0, padding: 0, color: Pallete.blue.base, width: '24px'}} />
        </IconButton>
      </Tooltip>
    ),
  };

  // Get the button color from the action type
  const getColorFromActionType = (
    type: 'destructive' | 'nondestructive' | 'neutral'
  ): MuiLoadingButtonProps => {
    switch (type) {
      case 'destructive':
        return {color: 'error', variant: 'text'};
      case 'nondestructive':
        return {color: 'primary', variant: 'contained'};
      case 'neutral':
        return {color: 'primary', variant: 'text'};
      default:
        return {color: 'primary', variant: 'text'};
    }
  };

  const dialogActionsSx: SxProps<Theme> = {
    padding: readonly ? '12px 95.5px' : '12px 22px',
    justifyContent: readonly ? 'center' : 'space-between',
    background:
      appContext.state.currentThemeMode === ThemeMode.Light
        ? Pallete.gray.lighter
        : Pallete.gray.base,
  };

  return (
    <Dialog
      {...{
        id,
        open,
        onClose: preventCloseClick ? () => {} : onClose,
        sx: {
          ...rest.sx,
          borderRadius: '20px',
          '.MuiPaper-root': {borderRadius: '16px'},
        },
        ...rest,
      }}
      TransitionComponent={Transition}
    >
      {title && (
        <>
          <Stack
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              paddingX: '20px',
              paddingTop: '24px',
              paddingBottom: '14px',
            }}
          >
            <DialogTitle
              sx={{
                flex: 1,
                fontFamily: 'Roboto',
                fontWeight: 600,
                fontSize: '22px',
                marginTop: '8px',
                padding: 0,
                lineHeight: '26px',
                color:
                  appContext.state.currentThemeMode === ThemeMode.Light
                    ? Pallete.gray.base
                    : Pallete.white.base,
              }}
              noWrap
            >
              {title}
            </DialogTitle>
            {actionButton && DEFAULT_BUTTON_ACTIONS[actionButton.type]}
          </Stack>
          <Divider sx={{margin: '0 20px'}} />
        </>
      )}
      <DialogContent
        sx={{
          display: 'grid',
          placeItems: 'center',
          overflowX: 'hidden',
          padding: 0,
          marginBottom: '20px',
          ...rest.sx,
        }}
      >
        <Box
          sx={{
            width: '100%',
            height: '100%',
            overflowY: 'auto',
            overflowX: 'hidden',
            padding: '20px',
          }}
        >
          {children}
        </Box>
      </DialogContent>
      <DialogActions sx={dialogActionsSx}>
        {actions.map(
          ({type, children, label, buttonType, textUpperCase, sx, disabled, loading, ...rest}) => (
            <LoadingButton
              {...getColorFromActionType(type)}
              key={`alert-modal-button-${type}-${children}`}
              loading={loading}
              loadingPosition={loading ? 'start' : undefined}
              disabled={disabled || loading}
              type={buttonType ?? 'button'}
              sx={{flex: 1, ...sx}}
              {...rest}
            >
              {label}
            </LoadingButton>
          )
        )}
      </DialogActions>
    </Dialog>
  );
};
